/**
 * GovernanceGuard - Proteção via Entra ID (Azure AD)
 * 
 * Toda a área /governance/* exige autenticação via Microsoft Entra ID.
 * Somente emails da lista ALLOWED_EMAILS têm acesso.
 * 
 * Fluxo:
 *   1. Chama /.auth/me (SWA Entra ID)
 *   2. Sem sessão → redireciona para /.auth/login/aad
 *   3. Email não autorizado → tela de acesso negado
 *   4. OK → renderiza children
 */
import React, { useState, useEffect } from 'react';
import { AZURE } from '../../services/api';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

// Emails autorizados para acessar o portal de governança
const ALLOWED_EMAILS = [
  'paulobrant@arenastats.com.br',
  'paulobrant@vukz.ai',
];

function useEntraAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/.auth/me')
      .then(res => res.json())
      .then(data => {
        const principal = data?.clientPrincipal;
        if (principal) {
          setUser({
            email: principal.userDetails,
            provider: principal.identityProvider,
            roles: principal.userRoles || [],
          });
          // Injeta X-Ops-Api-Key na instância AZURE para que chamadas da área
          // /governance/* passem pelo bypass de @require_auth no backend.
          const opsKey = process.env.REACT_APP_OPS_API_KEY;
          if (opsKey) {
            AZURE.defaults.headers.common['X-Ops-Api-Key'] = opsKey;
          }
          // Disponibiliza email para o interceptor de request (log de auditoria)
          window.__OPS_USER_EMAIL__ = principal.userDetails;
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return { user, loading };
}

export const GovernanceGuard = ({ children }) => {
  const { user, loading } = useEntraAuth();
  const { lang } = useGovernanceLang();

  const T = {
    pt: {
      authenticating: 'Autenticando via Microsoft Entra ID...',
      denied: 'Acesso não autorizado',
      deniedDesc: 'não tem permissão para acessar o portal de governança VUKZ.',
      deniedNote: 'Entre em contato com o administrador ou acesse com a conta correta.',
      switchAccount: 'Trocar conta',
    },
    en: {
      authenticating: 'Authenticating via Microsoft Entra ID...',
      denied: 'Access denied',
      deniedDesc: 'does not have permission to access the VUKZ governance portal.',
      deniedNote: 'Contact the administrator or sign in with the correct account.',
      switchAccount: 'Switch account',
    },
  }[lang] || {};

  // Aguarda chamada /.auth/me
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#064e3b] mx-auto mb-4" />
          <p className="text-gray-500 text-sm">{T.authenticating}</p>
        </div>
      </div>
    );
  }

  // Sem sessão Entra → redireciona para login Microsoft
  if (!user) {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/.auth/login/aad?post_login_redirect_uri=${returnUrl}`;
    return null;
  }

  // Email não está na allowlist
  if (!ALLOWED_EMAILS.includes(user.email?.toLowerCase())) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">🔒</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{T.denied}</h2>
          <p className="text-gray-500 text-sm mb-1">
            A conta <strong>{user.email}</strong> {T.deniedDesc}
          </p>
          <p className="text-gray-400 text-xs mt-4">
            {T.deniedNote}
          </p>
          <a
            href="/.auth/logout?post_logout_redirect_uri=/"
            className="inline-block mt-6 px-4 py-2 bg-[#064e3b] text-white text-sm rounded-lg"
          >
            {T.switchAccount}
          </a>
        </div>
      </div>
    );
  }

  // Expõe email para API interceptor
  window.__OPS_USER_EMAIL__ = user.email;

  return children;
};
